import React, { useState, useRef, useEffect } from 'react';
import './CGVAccept.css';
import CG from '../../pages/CG/CG';

interface CGVAcceptProps {
  acceptCGV: boolean;
  setAcceptCGV: (accept: boolean) => void;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  onConfirm?: () => void;
}

const CGVAccept: React.FC<CGVAcceptProps> = ({
  acceptCGV,
  setAcceptCGV,
  isOpen,
  onClose,
  onConfirm,
  className = '',
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  /* ------------------------------------------------------------------ */
  /* Vérifie si on a atteint le bas de l'iframe ----------------------- */
  /* ------------------------------------------------------------------ */
  const checkScrollPosition = () => {
    try {
      if (!iframeRef.current) return;

      const iframeDocument =
        iframeRef.current.contentDocument ||
        iframeRef.current.contentWindow?.document;

      if (!iframeDocument) return;

      const { scrollHeight, scrollTop, clientHeight } =
        iframeDocument.documentElement;

      // Calculer le pourcentage de défilement
      const calculatedProgress = Math.min(
        100,
        Math.round((scrollTop / (scrollHeight - clientHeight)) * 100) || 0
      );
      setScrollProgress(calculatedProgress);

      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setHasScrolledToBottom(true);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du défilement :', error);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Ajoute / enlève les écouteurs de scroll dans l'iframe ------------- */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!isOpen) return;

    setHasScrolledToBottom(false);
    setScrollProgress(0);

    const handleIframeLoad = () => {
      try {
        if (!iframeRef.current) return;
        const iframeDocument =
          iframeRef.current.contentDocument ||
          iframeRef.current.contentWindow?.document;

        iframeDocument?.addEventListener('scroll', checkScrollPosition);
      } catch (error) {
        console.error("Erreur lors de l'ajout de l'écouteur de défilement :", error);
      }
    };

    iframeRef.current?.addEventListener('load', handleIframeLoad);

    return () => {
      iframeRef.current?.removeEventListener('load', handleIframeLoad);
      try {
        const iframeDocument =
          iframeRef.current?.contentDocument ||
          iframeRef.current?.contentWindow?.document;

        iframeDocument?.removeEventListener('scroll', checkScrollPosition);
      } catch (error) {
        console.error('Erreur lors du nettoyage des écouteurs :', error);
      }
    };
  }, [isOpen]);

  /* ------------------------------------------------------------------ */
  /* Fonction pour gérer la confirmation ------------------------------ */
  /* ------------------------------------------------------------------ */
  const handleConfirm = () => {
    if (hasScrolledToBottom && acceptCGV) {
      if (onConfirm) {
        onConfirm(); // Appeler onConfirm si fourni
      }
      // Ne pas fermer automatiquement ici, laissons onConfirm gérer la fermeture
    }
  };

  /* ------------------------------------------------------------------ */
  /* Fonction pour gérer la fermeture sans confirmation -------------- */
  /* ------------------------------------------------------------------ */
  const handleCancel = () => {
    // Réinitialiser l'acceptation des CGV
    setAcceptCGV(false);
    onClose();
  };

  /* ------------------------------------------------------------------ */
  /* Rendu ------------------------------------------------------------- */
  /* ------------------------------------------------------------------ */
  if (!isOpen) return null;

  return (
    <div className="modal-overlay-cgv" onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      // Ne pas fermer en cliquant sur l'overlay
    }}>
      <div
        className={`modal-container-cgv cgv-container ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---------- En-tête ---------- */}
        <div className="modal-header">
          <h2>Conditions Générales</h2>
          <button className="close-button-cgv" onClick={handleCancel}>
            ×
          </button>
        </div>

        {/* ---------- Contenu ---------- */}
        <div className="modal-content-cgv">
          <div className="scroll-hint-container">
            <div className={`scroll-hint-cgv ${hasScrolledToBottom ? 'hidden' : ''}`}>
              Veuillez défiler jusqu'à la fin du document pour pouvoir accepter
              les CG
            </div>
          </div>

          {/* Indicateur de progression de défilement */}
          <div className="scroll-progress-container">
            <div
              className="scroll-progress-bar"
              style={{ width: `${scrollProgress}%` }}
            ></div>
            <div className="scroll-progress-text">
              {scrollProgress < 100
                ? `Progression: ${scrollProgress}%`
                : "✓ Document lu en entier"}
            </div>
          </div>

          <iframe
            ref={iframeRef}
            src="/cgv"
            title="Conditions Générales"
            className="cgv-iframe"
          />

        </div>

        {/* ---------- Pied de page ---------- */}
        <div className="modal-footer-cgv">
          <div className="footer-actions-cgv">
            <label className="checkbox-label-cgv">
              <input
                type="checkbox"
                id="cgv-checkbox"
                checked={acceptCGV}
                onChange={() => hasScrolledToBottom && setAcceptCGV(!acceptCGV)}
                className="cgv-checkbox"
                disabled={!hasScrolledToBottom}
              />
              <span className="checkbox-text-cgv">
                J'accepte les Conditions Générales
              </span>
            </label>

            <button
              className="confirm-button-cgv"
              onClick={handleConfirm}
              disabled={!acceptCGV || !hasScrolledToBottom}
            >
              Confirmer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CGVAccept;
